<template>
  <div id="auth" class="min-h-dvh flex items-center justify-center bg-[#13191d] p-4">
    <div class="w-full max-w-md bg-black/40 p-8 rounded-sm" v-if="!registrationSuccess">
      <form @submit.prevent class="flex flex-col">
        
        <div class="text-center mb-6">
          <h1 class="text-2xl font-bold text-gray-900 dark:text-white" v-if="!showPasswordInput">Welcome</h1>
          <h1 class="text-2xl font-bold text-gray-900 dark:text-white" v-else>Sign up</h1>
          
          <p class="mt-2 text-sm/6 text-gray-600 dark:text-gray-400" v-if="!showPasswordInput">Enter your email to get started</p>
          <p class="mt-2 text-sm/6 text-gray-600 dark:text-gray-400" v-else>Enter your details to get started</p>
        </div>

        <div v-if="error" class="bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 p-4 rounded-sm text-sm/6 border border-red-200 dark:border-red-900/30 mb-6">
          {{ error }}
        </div>
        
        <div v-if="!code" class="bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 p-4 rounded-sm text-sm/6 border border-red-200 dark:border-red-900/30 mb-6">
           <p class="font-bold">Company code missing</p>
           <p>Please contact your company for registration link</p>
        </div>

                <div class="flex flex-col gap-2 mb-6">
           <label class="text-sm/6 font-medium text-gray-900 dark:text-gray-200">Email</label>
           <input
             autofocus
             placeholder="john.doe@mail.com"
             v-model="form.username"
             :disabled="showPasswordInput"
             @keyup.enter="handleEnter"
             required
             type="email"
             class="block w-full rounded-sm border-0 py-2 px-4 text-white bg-white/5 shadow-xs ring-1 ring-inset ring-gray-300 dark:ring-gray-700 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600  sm:text-sm/6 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
           >
        </div>

        <div 
            class="grid transition-all duration-300 ease-in-out"
            :class="showPasswordInput ? 'grid-rows-[1fr] opacity-100 mb-6' : 'grid-rows-[0fr] opacity-0 mb-0'"
        >
            <div class="overflow-hidden">
                <div class="flex flex-col gap-2">
                    <label class="text-sm/6 font-medium text-gray-200">Password</label>
                    <input
                        ref="passwordInput"
                        placeholder="Password"
                        type="password"
                        v-model="form.password"
                        @keyup.enter="handleEnter"
                        class="block w-full rounded-sm border-0 py-2 px-4 text-white bg-white/5 shadow-xs ring-1 ring-inset ring-gray-300 dark:ring-gray-700 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600  sm:text-sm/6 transition-colors"
                    >
                </div>
            </div>
        </div>

         <button
          type="button"
          :disabled="isBtnLoading || (!showPasswordInput && !code)"
          @click="showPasswordInput ? submitRegistration() : validateEmail()"
          class="flex w-full justify-center rounded-sm bg-brand px-3 py-2 text-sm/6 font-bold text-white! shadow-xs hover:bg-black transition-all duration-200 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed items-center gap-2 cursor-pointer"
        >
          <span v-if="isBtnLoading" class="inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-current border-r-transparent motion-reduce:animate-[spin_1.5s_linear_infinite]" role="status"></span>
          {{ showPasswordInput ? 'Create Account' : 'Get started' }}
        </button>

      </form>
    </div>

    <div class="w-full max-w-md bg-white dark:bg-gray-800 p-8 rounded-sm shadow-xs flex flex-col gap-6 text-center" v-else>
      <div>
        <h1 class="text-2xl font-bold text-gray-900 dark:text-white">Verify your email</h1>
        <p class="mt-2 text-sm/6 text-gray-600 dark:text-gray-400">
          We have sent an email with a confirmation link to your email address. In
          order to complete the sign-up process, please click the confirmation link.
        </p>
      </div>
      <button
        type="button"
        @click="goToLogin"
        class="flex w-full justify-center rounded-sm bg-brand px-3 py-2 text-sm/6 font-semibold text-white! shadow-xs hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand cursor-pointer transition-colors"
      >Go to login page
      </button>
    </div>
  </div>
</template>

<script>
// @ is an alias to /src
import { signUp } from 'aws-amplify/auth';

export default {
  data() {
    return {
      showPasswordInput: false,
      registrationSuccess: false,
      form: {
        username: '',
        password: '',
      },
      code: null,
      user: null,
      isBtnLoading: false,
      error: null,
    };
  },

  computed: {
    // Removed computedRules as they were for Element UI
  },

  created() {
  },

  mounted() {
    const hasCodes = this.$route.params.code.split(':').length === 2;
    if (!hasCodes) return;
    this.code = this.$route.params.code;
  },

  methods: {
    handleEnter() {
      if (this.showPasswordInput) {
        this.submitRegistration();
      } else {
        this.validateEmail();
      }
    },

    async validateEmail() {
      // Validate email format manually
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!this.form.username || !emailRegex.test(this.form.username)) {
        this.error = 'Incorrect email address.';
        return;
      }
      
      this.error = null;
      this.showPasswordInput = true;
      // Focus password input after transition
      this.$nextTick(() => {
        if (this.$refs.passwordInput) {
          this.$refs.passwordInput.focus();
        }
      });
    },

    async submitRegistration() {
      // Validate form manually
      if (!this.form.username) {
        this.error = 'Incorrect email address.';
        return;
      }

      if (!this.form.password) {
        this.error = 'Please enter your password';
        return;
      }

      if (this.form.password.length < 8) {
        this.error = 'Password is too short';
        return;
      }

      try {
        this.isBtnLoading = true;
        this.form.username = this.form.username.toLowerCase();
        const result = await signUp({
          username: this.form.username,
          password: this.form.password,
          options: {
            userAttributes: {
              email: this.form.username,
              'custom:client': this.code,
            },
          },
        });
        const isSignUpSuccess = !!result.userId || !!result.user || !!result.UserSub;

        if (!isSignUpSuccess) {
          this.error = 'Something went wrong try again';
          this.isBtnLoading = false;
          return;
        }

        this.user = result.user || result;
        this.registrationSuccess = true;
        this.isBtnLoading = false;
      } catch (error) {
        const hasErrorCode = !!error.code;
        this.isBtnLoading = false;
        if (hasErrorCode) {
          this.error = error.message;
          return;
        }
        this.error = error;
      }
    },

    goToLogin() {
      this.$router.push({ path: '/login' });
    },
  },
};
</script>
